import { Injectable } from '@angular/core';
/**
 * Service for managing URL-specific HTTP headers.
 * Supports default headers that apply globally unless overridden by URL-specific ones.
 */
@Injectable({
  providedIn: 'root',
})
export class HeaderService {

  /**
   * The key used for default headers.
   */
  private readonly DEFAULT_KEY = 'default' as const;
  /**
   * Store for headers, organized by URL.
   */
  private headers: { [url: string]: { [key: string]: string } } = {};

  /**
   * Sets a header for the given URL (or 'default' if no URL-specific exists).
   * @param url The API URL (normalized internally).
   * @param key The header key (e.g., 'Authorization').
   * @param value The header value (skipped if null or undefined).
   */
  public setHeader(url: string, key: string, value: string): void {
    if (value == null) { // Allows empty strings; change to !== if falsy should skip
      return;
    }
    if (!this.headers[url]) {
      this.headers[url] = {};
    }
    this.headers[url][key] = value;
  }

  /**
   * Clears a header for the given URL and returns its previous value.
   * @param url The API URL (normalized internally).
   * @param key The header key.
   * @returns The previous value, or undefined if not found.
   */
  public clearHeader(url: string, key: string): string | undefined {
    if (this.headers[url]?.hasOwnProperty(key)) {
      const value = this.headers[url][key];
      delete this.headers[url][key];
      // Clean up empty URL object
      if (Object.keys(this.headers[url]).length === 0) {
        delete this.headers[url];
      }
      return value;
    }
    return undefined;
  }

  /**
   * Gets all headers for the URL, merging with defaults (URL-specific overrides defaults).
   * Removes Authorization if no valid JWT token is present.
   * @param url The API URL (normalized internally).
   * @returns A cloned object of merged headers.
   */
  public getHeaders(url: string): { [key: string]: string } {
    let mergedHeaders: { [key: string]: string } = {};

    // Start with defaults
    const defaults = this.headers[this.DEFAULT_KEY] || {};
    mergedHeaders = { ...defaults };

    // Override with URL-specific (checking for suffix matches)
    // We iterate over all registered keys to see if the requested URL ends with them.
    // This handles cases where setHeader uses a partial path (e.g. 'v1/auth/sign')
    // but the interceptor sees the full URL (e.g. 'http://localhost:400/v1/auth/sign').
    Object.keys(this.headers).forEach((registeredUrl) => {
      if (registeredUrl !== this.DEFAULT_KEY && url.endsWith(registeredUrl)) {
        mergedHeaders = { ...mergedHeaders, ...this.headers[registeredUrl] };
      }
    });

    // Remove Authorization if no token
    if (mergedHeaders['Authorization'] && !this.hasValidToken()) {
      delete mergedHeaders['Authorization'];
    }
    return { ...mergedHeaders }; // Clone for immutability
  }

  /**
   * Verifies if a header exists for the URL.
   * @param url The API URL (normalized internally).
   * @param key The header key.
   * @returns True if the header exists.
   */
  public verifyHeader(url: string, key: string): boolean {
    return !!this.headers[url]?.hasOwnProperty(key);
  }

  /**
   * Checks for a valid JWT token in storage.
   * @private
   */
  private hasValidToken(): boolean {
    return !!localStorage.getItem('jwt_token') || !!sessionStorage.getItem('GuestUserJWT'); // Note: Typo in 'GusetUserJWT'?
  }
}
